// ── Project Settings Page ──

import { checkAuth, getUser } from '../auth.js';
import { api } from '../api.js';
import { showToast, openModal, closeModal, esc, getThemes, colorSchemeSwatch } from '../ui.js';
import { navigate, getQueryParams, setQueryParams, getNavigationId } from '../router.js';
import { initLayout, getContentEl, updateLayoutContext, refreshProjectList } from '../layout.js';
import { loadStatuses, invalidateStatuses, reloadStatuses, statusLabel, statusBadgeAttrs, setStatusLocale, transliterateToSlug } from '../lib/statuses.js';
import { invalidateProject } from '../lib/project.js';
import { can, projectWithPermissions, effectiveRole, roleLabel, ROLES, ROLE_LABELS, ROLE_DESCRIPTIONS } from '../lib/permissions.js';
import { renderAvatarHtml } from '../utils.js';
import { TASK_GROUP_LABELS, DEFAULT_TASK_GROUP_LABEL, groupLabelPlural, groupLabelSingular } from '../lib/task-group-label.js';
import { VCS_GIT_FLAGS, buildVcsPatchBody } from '../lib/vcs-form.js';
import { renderPermissionModeSelect, bindPermissionModeSelects, syncPermissionModeSelect, permissionModeLabel, permissionModeHint } from '../lib/agent-permission-mode.js';
import { disableForSave, noteFocusBeforeRender } from '../lib/autosave-focus.js';
import { dropIndex } from '../lib/drag-order.js';
import { mountWebhooks, webhookText } from '../lib/webhooks.js';

// Catppuccin token names offered in the Workflow tab's color picker — same set (and
// same hex values) as api/src/lib/task-statuses.js's DEFAULT_STATUSES colors plus the
// rest of the palette this app's own `--c-<token>` CSS custom properties expose
// (styles.css). A status's `color` column stores one of these bare names.
const WORKFLOW_COLOR_TOKENS = [
  'rosewater', 'flamingo', 'pink', 'mauve', 'red', 'maroon', 'peach', 'yellow',
  'green', 'teal', 'sky', 'blue', 'lavender', 'overlay0', 'overlay1',
];

// ── Color-swatch popover (C1189) ──
// Replaces the old `.wf-chip-color` <select> of token names with a visual grid. Same
// house popover idiom as sprint-board.js's showStatusDropdown()/removeStatusDropdown():
// a `position:fixed` node appended to document.body (so it escapes any ancestor's
// overflow/stacking), closed by a mousedown listener outside it, installed via
// setTimeout(...,0) so the click that opened it doesn't also close it.
//
// Keyboard (TPT9178): sitting at the end of <body>, the picker is far from its trigger in tab
// order, so opening it moves focus onto the current color's swatch and keeps it inside: the
// arrow keys move along the grid, Home/End go to the first/last swatch and Enter/Space pick
// (each swatch is a <button>, so they click it). Escape closes it without a pick, and Tab or
// Shift+Tab closes it too; both hand focus back to the trigger, from where Tab moves on as if
// the picker sat right after the trigger.

// Columns of `.color-swatch-picker`'s grid (styles.css) — the arrow keys move along them.
const COLOR_PICKER_COLUMNS = 5;

// The open picker, the trigger that opened it and its outside-press listener. Every close — a
// pick, Escape, Tab, an outside press, opening another picker, the page's teardown — goes
// through closeColorPicker(), which drops all three, so no document listener outlives the picker.
let colorPicker = null;
let colorPickerTrigger = null;
let colorPickerOutsideHandler = null;

// With `refocus`, a picker that has keyboard focus gives it back to its trigger as it closes —
// removing the picker drops focus from the swatch to the body. When a Workflow table re-render
// replaced the trigger while the picker was open, its copy in the new markup takes over.
function closeColorPicker(refocus = false) {
  const picker = colorPicker;
  const trigger = colorPickerTrigger;
  if (colorPickerOutsideHandler) document.removeEventListener('mousedown', colorPickerOutsideHandler);
  colorPicker = colorPickerTrigger = colorPickerOutsideHandler = null;
  if (!picker) return;
  const hadFocus = picker.contains(document.activeElement);
  picker.remove();
  const current = trigger.isConnected ? trigger : document.querySelector(workflowFocusSelector(trigger));
  current?.setAttribute('aria-expanded', 'false');
  if (refocus && hadFocus) current?.focus();
}

// The picker's swatch buttons: one per WORKFLOW_COLOR_TOKENS entry, the current color ringed
// (`.selected`) and pressed.
export function colorSwatchesHtml(currentColor) {
  return WORKFLOW_COLOR_TOKENS.map(name => {
    const label = statusLabel(name);
    const current = name === currentColor;
    return `<button type="button" class="color-swatch${current ? ' selected' : ''}" style="--swatch-color:var(--c-${name})" data-color="${name}" title="${esc(label)}" aria-label="${esc(label)}" aria-pressed="${current}"></button>`;
  }).join('');
}

// Index of the swatch a key moves focus to from swatch `index` of `count`, laid out `columns` to
// a row, or -1 for a key that moves nothing. Left/Right step through the swatches in reading
// order and wrap from the last to the first; Up/Down stay in the column and wrap between the top
// and bottom rows; Home/End go to the first/last swatch.
export function swatchIndexForKey(key, index, count, columns = COLOR_PICKER_COLUMNS) {
  switch (key) {
    case 'ArrowRight': return (index + 1) % count;
    case 'ArrowLeft': return (index - 1 + count) % count;
    case 'ArrowDown': return index + columns < count ? index + columns : index % columns;
    case 'ArrowUp': {
      if (index >= columns) return index - columns;
      // The same column of the last row, or of the row above it when the last row is short.
      const below = Math.floor((count - 1) / columns) * columns + index;
      return below < count ? below : below - columns;
    }
    case 'Home': return 0;
    case 'End': return count - 1;
    default: return -1;
  }
}

function openColorPicker(triggerEl, currentColor, onPick) {
  closeColorPicker();
  const rect = triggerEl.getBoundingClientRect();
  const picker = document.createElement('div');
  picker.className = 'color-swatch-picker';
  picker.setAttribute('role', 'group');
  picker.setAttribute('aria-label', 'Status color');
  const left = Math.min(rect.left, window.innerWidth - 180);
  picker.style.cssText = `position:fixed;top:${rect.bottom + 4}px;left:${Math.max(4, left)}px;`;
  picker.innerHTML = colorSwatchesHtml(currentColor);
  document.body.appendChild(picker);
  // No room below the trigger (the add form's sits at the foot of the page): open above it, so
  // no swatch the arrow keys reach is off-screen — a fixed picker never scrolls into view.
  const height = picker.offsetHeight;
  if (rect.bottom + 4 + height > window.innerHeight && rect.top - 4 - height >= 0) {
    picker.style.top = `${rect.top - 4 - height}px`;
  }
  colorPicker = picker;
  colorPickerTrigger = triggerEl;
  triggerEl.setAttribute('aria-expanded', 'true');
  (picker.querySelector('.color-swatch.selected') || picker.querySelector('.color-swatch')).focus();

  picker.addEventListener('click', (e) => {
    const swatch = e.target.closest('.color-swatch');
    if (!swatch) return;
    onPick(swatch.dataset.color);
    // Focus goes back to the trigger, so the Workflow table's re-render after the save keeps it (TPT9174).
    closeColorPicker(true);
  });

  picker.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeColorPicker(true); return; }
    // Not prevented: the browser's Tab then moves on from the trigger closeColorPicker() focuses.
    if (e.key === 'Tab') { closeColorPicker(true); return; }
    if (e.altKey || e.ctrlKey || e.metaKey) return; // e.g. Cmd+Left keeps its browser meaning
    const swatches = [...picker.querySelectorAll('.color-swatch')];
    const index = swatchIndexForKey(e.key, swatches.indexOf(e.target), swatches.length);
    if (index < 0) return;
    e.preventDefault();
    swatches[index].focus();
  });

  setTimeout(() => {
    if (colorPicker !== picker) return;
    colorPickerOutsideHandler = (ev) => {
      if (!picker.contains(ev.target)) closeColorPicker();
    };
    document.addEventListener('mousedown', colorPickerOutsideHandler);
  }, 0);
}

function colorTriggerHtml(color, attrs) {
  const style = color ? `var(--c-${color})` : 'var(--c-overlay1)';
  const attrStr = Object.entries(attrs || {}).map(([k, v]) => `${k}="${esc(String(v))}"`).join(' ');
  return `<button type="button" class="wf-color-trigger" style="--status-color:${style}" ${attrStr} title="Change color" aria-label="Change color" aria-haspopup="true" aria-expanded="false"></button>`;
}

function formatDate(d) {
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function memberRoleOptions(selected = 'editor') {
  return ROLES.map(role => `<option value="${role}"${role === selected ? ' selected' : ''}>${roleLabel(role)}</option>`).join('');
}

// Role pill, tinted per role (styles.css `.role-badge--*`). An unrecognized role keeps
// its raw text on the neutral tint instead of minting a class name from server data.
export function roleBadgeHtml(role) {
  const modifier = Object.hasOwn(ROLE_LABELS, role) ? role : 'unknown';
  return `<span class="badge role-badge role-badge--${modifier}">${esc(roleLabel(role))}</span>`;
}

// `meId` marks the caller's own row ("You") so a manager can tell which role picker
// changes their own access. Each role picker's id names its member (or pending invitation),
// so the picker a refresh renders after a role save takes back keyboard focus.
export function renderMembersTable(members, canManageMembers, meId = null) {
  if (!members.length) {
    return '<p class="text-muted">No members yet</p>';
  }
  return `<div class="members-table-wrap"><table class="members-table">
    <thead><tr>
      <th>Member</th><th>Email</th><th>Role</th><th>Joined</th>
      ${canManageMembers ? '<th></th>' : ''}
    </tr></thead>
    <tbody>${members.map(m => {
      const isPending = m.status === 'pending';
      const isMe = !isPending && meId != null && String(m.user_id) === String(meId);
      const editable = canManageMembers && m.role !== 'owner';
      const roleCell = (editable
        ? `<select class="form-select form-select-sm member-role-select" id="member-role-${isPending ? `invite-${esc(m.email)}` : m.user_id}" aria-label="Role for ${esc(m.name || m.email)}" data-user-id="${isPending ? 0 : m.user_id}" data-email="${esc(m.email)}" data-role="${esc(m.role)}">${memberRoleOptions(m.role)}</select>`
        : roleBadgeHtml(m.role))
        + (isPending ? ' <span class="badge badge-pending">Pending</span>' : '');
      const removeBtn = editable
        ? `<button class="btn btn-ghost btn-sm remove-member-btn" data-user-id="${isPending ? 0 : m.user_id}" data-email="${esc(m.email)}" data-name="${esc(m.name || m.email)}">Remove</button>`
        : '';
      return `<tr data-user-id="${m.user_id || 0}"${isMe ? ' class="is-me"' : ''}>
        <td><div class="member-identity">
          ${renderAvatarHtml(isPending ? { email: m.email } : m, 28)}
          <span class="member-name${isPending ? ' text-muted' : ''}">${esc(m.name || (isPending ? 'Invited' : '—'))}</span>
          ${isMe ? '<span class="member-you">You</span>' : ''}
        </div></td>
        <td class="member-email">${esc(m.email)}</td>
        <td class="member-role-cell">${roleCell}</td>
        <td class="text-muted">${formatDate(m.created_at)}</td>
        ${canManageMembers ? `<td class="member-actions">${removeBtn}</td>` : ''}
      </tr>`;
    }).join('')}</tbody>
  </table></div>`;
}

// "What each role can do" — shown to every member under the roster.
export function renderRoleLegend() {
  return `<div class="role-legend" aria-label="What each role can do">
    <div class="role-legend-title">What each role can do</div>
    <dl class="role-legend-list">${['owner', ...[...ROLES].reverse()].map(role => `
      <div class="role-legend-row">
        <dt>${roleBadgeHtml(role)}</dt>
        <dd>${esc(ROLE_DESCRIPTIONS[role])}</dd>
      </div>`).join('')}
    </dl>
  </div>`;
}

const SETTINGS_TAB_LABELS = {
  general: 'General', members: 'Members', workflow: 'Workflow', 'version-control': 'Version Control',
  memory: 'Memory', appearance: 'Appearance', notifications: 'Notifications', danger: 'Danger Zone',
  webhooks: 'Webhooks',
};

// Which tabs a member gets and where they land. Members (a read-only roster without
// member:manage) and Notifications (the caller's own preferences) are for everyone;
// every project-wide setting needs settings:manage, Danger Zone needs project:delete.
export function settingsTabsFor({ canManageSettings, canManageMembers, canDeleteProject, canManageWebhooks = false }) {
  const tabs = [
    ...(canManageSettings ? ['general'] : []),
    'members',
    ...(canManageSettings ? ['workflow', 'version-control', 'memory', 'appearance'] : []),
    'notifications',
    ...(canManageWebhooks ? ['webhooks'] : []),
    ...(canDeleteProject ? ['danger'] : []),
  ];
  const defaultTab = canManageSettings ? 'general' : (canManageMembers ? 'members' : 'notifications');
  return { tabs, defaultTab };
}

export function settingsTabFromQuery(tabs, defaultTab, params) {
  const requested = params.get('tab');
  return tabs.includes(requested) ? requested : defaultTab;
}

// C1267 — grouped by mode (Light/Dark) instead of one flat list: 19 named palettes as a
// single grid reads as an unstructured wall on narrow viewports (repeat(auto-fit,
// minmax(220px,1fr)) reflows to one column). Group headings should replace the "Light › "/
// "Dark › " prefix baked into each theme.label (that prefix exists for the Task App's flat
// #settings-theme-select, which has no grouping of its own) — stripModePrefix() (C1273) does
// that at render time; the prefix stays in ui.js's THEMES since the Task App still needs it.
const stripModePrefix = (label) => label.replace(/^(?:Light|Dark)\s*›\s*/, '');

// ── Version Control tab (C1214) ──
// Three git-only suboptions, each toggled by a checkbox shown only while vcs_type='git'.
// VCS_GIT_FLAGS + the PATCH-body assembly rule live in lib/vcs-form.js (C1344, pulled
// out of this file to be unit-tested standalone — same precedent as api/src/lib/
// vcs-settings.js's server-side buildVcsPatch()); imported above.

// ── Notifications tab ──
// Personal project overrides are caller-owned and nullable. Project defaults require
// settings:manage. The API returns all three source layers so controls can show the
// saved choice rather than mistaking an inherited effective value for an override.
const NOTIFICATION_MODE_OPTIONS = [
  { value: 'off', labelKey: 'off' },
  { value: 'all', labelKey: 'all' },
  { value: 'assigned', labelKey: 'assigned' },
  { value: 'authored', labelKey: 'authored' },
];
const DEFAULT_NOTIFICATION_MODE = 'assigned'; // mirrors api/src/lib/notification-modes.js
const NOTIFICATION_TEXT = {
  en: {
    personalTitle: 'Your project notifications',
    personalIntro: 'These choices affect only your notifications from this project.',
    projectTitle: 'Project-wide defaults',
    projectIntro: 'These defaults apply to members who have not set an account or personal project preference.',
    scopeNotice: 'You are editing two separate sets of settings: your own project preferences and project-wide defaults for all members.',
    precedence: 'Personal project preferences override account preferences, which override project-wide defaults.',
    inherit: 'Inherit account or project default',
    current: 'Currently',
    personalSource: 'your project preference',
    accountSource: 'your account preference',
    projectSource: 'the project-wide default',
    digest: 'Daily digest',
    transactional: 'Transactional',
    digestHint: 'One summary email per day of board activity in this project.',
    transactionalEmailHint: "Task-update emails are sent to your account email for tasks you're subscribed to. Choose Off to stop them.",
    off: 'Off', all: 'All Tasks', assigned: 'Assigned To Me', authored: 'Authored by Me',
    loadError: 'Notification preferences could not be loaded. Reload this page to try again.',
  },
  uk: {
    personalTitle: 'Ваші сповіщення проєкту',
    personalIntro: 'Ці налаштування впливають лише на ваші сповіщення з цього проєкту.',
    projectTitle: 'Налаштування проєкту за замовчуванням',
    projectIntro: 'Ці налаштування діють для учасників, які не задали налаштувань облікового запису або особистих налаштувань проєкту.',
    scopeNotice: 'Ви редагуєте два окремі набори налаштувань: власні налаштування проєкту та налаштування проєкту за замовчуванням для всіх учасників.',
    precedence: 'Особисті налаштування проєкту мають перевагу над налаштуваннями облікового запису, а ті — над налаштуваннями проєкту за замовчуванням.',
    inherit: 'Успадкувати налаштування облікового запису або проєкту',
    current: 'Зараз',
    personalSource: 'ваше налаштування проєкту',
    accountSource: 'ваше налаштування облікового запису',
    projectSource: 'налаштування проєкту за замовчуванням',
    digest: 'Щоденний огляд',
    transactional: 'Окремі сповіщення',
    digestHint: 'Один підсумковий лист на день про активність у цьому проєкті.',
    transactionalEmailHint: 'Листи про оновлення завдань надсилаються на електронну адресу вашого облікового запису для завдань, на сповіщення про які ви підписані. Виберіть «Вимкнено», щоб припинити їх отримувати.',
    off: 'Вимкнено', all: 'Усі завдання', assigned: 'Призначені мені', authored: 'Створені мною',
    loadError: 'Не вдалося завантажити налаштування сповіщень. Перезавантажте сторінку та спробуйте ще раз.',
  },
};
const NOTIFICATION_FIELDS = [
  { field: 'digest_mode', labelKey: 'digest', hintKey: 'digestHint' },
  { field: 'transactional_mode', labelKey: 'transactional', hintKey: 'transactionalEmailHint' },
];

// The web SPA has no general i18n service; Settings follows the existing project-language
// lookup pattern used by status labels. Unknown/missing languages fall back to English.
export function notificationText(language, key) {
  const locale = NOTIFICATION_TEXT[language] ? language : 'en';
  return NOTIFICATION_TEXT[locale][key] || NOTIFICATION_TEXT.en[key] || key;
}

export function notificationEffective(layers, field) {
  const personal = layers.personal_settings?.[field];
  const account = layers.account_settings?.[field];
  const project = layers.project_settings?.[field];
  if (personal != null) return { value: personal, source: 'personalSource' };
  if (account != null) return { value: account, source: 'accountSource' };
  return { value: project ?? DEFAULT_NOTIFICATION_MODE, source: 'projectSource' };
}

function notificationControls(layers, language, scope) {
  const personal = scope === 'personal';
  const settings = personal ? layers.personal_settings : layers.project_settings;
  return NOTIFICATION_FIELDS.map(f => {
    const id = `settings-${scope}-${f.field}`;
    const selected = settings?.[f.field] ?? (personal ? 'inherit' : DEFAULT_NOTIFICATION_MODE);
    const effective = notificationEffective(layers, f.field);
    return `<div class="form-group notification-setting-field">
      <label class="form-label" for="${id}">${esc(notificationText(language, f.labelKey))}</label>
      <select class="form-select" id="${id}" data-notification-scope="${scope}" data-notification-field="${f.field}">
        ${personal ? `<option value="inherit"${selected === 'inherit' ? ' selected' : ''}>${esc(notificationText(language, 'inherit'))}</option>` : ''}
        ${NOTIFICATION_MODE_OPTIONS.map(o => `<option value="${o.value}"${selected === o.value ? ' selected' : ''}>${esc(notificationText(language, o.labelKey))}</option>`).join('')}
      </select>
      <p class="text-muted mb-2">${esc(notificationText(language, f.hintKey))}</p>
      ${personal ? `<p class="text-muted notification-effective">${esc(notificationText(language, 'current'))}: ${esc(notificationText(language, effective.value))} (${esc(notificationText(language, effective.source))}).</p>` : ''}
    </div>`;
  }).join('');
}

export function buildNotificationsTab(layers, language = 'en', canManageDefaults = false) {
  if (!layers) return `<div class="card"><p role="alert">${esc(notificationText(language, 'loadError'))}</p></div>`;
  return `${canManageDefaults ? `<p class="notification-scope-notice" role="note">${esc(notificationText(language, 'scopeNotice'))}</p>` : ''}
    <div class="card">
      <h3>${esc(notificationText(language, 'personalTitle'))}</h3>
      <p class="text-muted mb-2">${esc(notificationText(language, 'personalIntro'))}</p>
      <p class="text-muted mb-2">${esc(notificationText(language, 'precedence'))}</p>
      ${notificationControls(layers, language, 'personal')}
    </div>
    ${canManageDefaults ? `<div class="card notification-defaults-card">
      <h3>${esc(notificationText(language, 'projectTitle'))}</h3>
      <p class="text-muted mb-2">${esc(notificationText(language, 'projectIntro'))}</p>
      ${notificationControls(layers, language, 'project')}
    </div>` : ''}`;
}

export function patchNotificationSetting(client, projectId, field, value) {
  return client.patch('/projects/' + projectId + '/notification-settings', { [field]: value });
}

export function patchProjectNotificationDefault(client, projectId, field, value) {
  return client.patch('/projects/' + projectId + '/notification-settings/defaults', { [field]: value });
}

export async function saveNotificationChoice(client, projectId, layers, scope, field, value) {
  if (scope === 'personal') {
    const res = await patchNotificationSetting(client, projectId, field, value);
    if (!res?.personal_settings) throw new Error('Invalid notification settings response');
    return res;
  }
  if (scope === 'project') {
    const res = await patchProjectNotificationDefault(client, projectId, field, value);
    if (!res?.settings) throw new Error('Invalid project defaults response');
    return { ...layers, project_settings: res.settings };
  }
  throw new Error('Unknown notification settings scope');
}

// C1264/C1267/C1303 — project color_scheme selector, now the Appearance tab's ONLY
// control (moved off the General tab's <select>, C1303 — a project's palette used to be
// editable from two places at once, one of which secretly wrote the *workspace*
// preference instead — see tt-web-theme.md). `'default'` means "no project override" —
// the project falls back to this browser's fixed light/dark toggle (see ui.js) rather
// than rendering as a real palette; it is intentionally NOT one of the grouped cards
// below. The API's `api/src/lib/color-schemes.js` COLOR_SCHEMES is the true source of
// valid values, but the web bundle can't require() the server lib — so instead of
// hand-mirroring that list a second time, this derives its groups from ui.js's own
// THEMES registry (getThemes()), which is itself the client-side "what can this app
// actually paint" list. One fewer hand-kept list to drift.
const DEFAULT_COLOR_SCHEME = 'default';
const COLOR_SCHEME_GROUPS = [
  { label: 'Light', schemes: getThemes().filter(t => t.mode === 'light') },
  { label: 'Dark', schemes: getThemes().filter(t => t.mode === 'dark') },
];

// Keep the scheme visible to every member; settings:manage gates radio edits.
function renderColorSchemeOptions(current, canEdit) {
  const cardHtml = (id, label) => `<label class="theme-option${canEdit ? '' : ' is-disabled'}">
    <input type="radio" name="settings-theme" value="${esc(id)}" ${id === current ? 'checked' : ''} ${canEdit ? '' : 'disabled'}>
    <span class="theme-option-swatch" style="--swatch:${esc(colorSchemeSwatch(id))}"></span>
    <span class="theme-option-label">${esc(label)}</span>
  </label>`;
  const defaultHtml = `<div class="theme-options">${cardHtml(DEFAULT_COLOR_SCHEME, 'Default (follow the light/dark toggle)')}</div>`;
  const groupsHtml = COLOR_SCHEME_GROUPS.map(g => {
    if (!g.schemes.length) return '';
    // stripModePrefix() (C1273) — same "heading already says Light/Dark" reasoning the
    // group headings exist for.
    return `<h4 class="theme-options-group-title">${esc(g.label)}</h4>
    <div class="theme-options">${g.schemes.map(s => cardHtml(s.id, stripModePrefix(s.label))).join('')}</div>`;
  }).join('');
  return defaultHtml + groupsHtml;
}

// `project` fields (vcs_type / vcs_*_enabled) come straight off `GET /projects/:id`, a
// `SELECT *` — the three flags arrive as MySQL TINYINT (`0`/`1`), not real booleans, hence
// the truthiness checks below rather than `=== true`. `vcs_type` is `'git'` | `'svn'` | `null`.
function renderVersionControlTab(project) {
  const vcsType = project.vcs_type || '';
  return `<div class="card">
    <h3>Version Control</h3>
    <p class="text-muted mb-2">Let task-execution agents use git (worktrees, commits, pull requests) or svn for this project. Disabled by default.</p>
    <form id="vcs-form">
      <div class="vcs-options" id="vcs-type-options">
        <label class="vcs-option">
          <input type="radio" name="vcs-type" value="" ${vcsType === '' ? 'checked' : ''}>
          <span class="vcs-option-label">Disabled</span>
        </label>
        <label class="vcs-option">
          <input type="radio" name="vcs-type" value="git" ${vcsType === 'git' ? 'checked' : ''}>
          <span class="vcs-option-label">Git</span>
        </label>
        <label class="vcs-option">
          <input type="radio" name="vcs-type" value="svn" ${vcsType === 'svn' ? 'checked' : ''}>
          <span class="vcs-option-label">SVN</span>
        </label>
      </div>
      <div class="vcs-suboptions" id="vcs-git-options" ${vcsType === 'git' ? '' : 'hidden'}>
        ${VCS_GIT_FLAGS.map(f => `<label class="vcs-suboption">
          <input type="checkbox" data-field="${f.field}" ${project[f.field] ? 'checked' : ''}>
          <span class="vcs-suboption-label">${esc(f.label)}</span>
          <span class="vcs-suboption-hint text-muted">${esc(f.hint)}</span>
        </label>`).join('')}
      </div>
      <button type="submit" class="btn btn-primary" id="save-vcs-btn">Save</button>
    </form>
  </div>`;
}

// ── Sprints card captions (C1456) ──
// Every caption in the Sprints card is derived from the project's own grouping noun
// (task_group_label) instead of a hardcoded "Sprint(s)" — so picking "Batches" reads
// "Show Batches", not "Show Sprints". Shared by the initial render (renderSprintsCard())
// and the live repaint on select change (paintSprintsCardCaptions()) below, so the two
// can never drift out of sync. groupLabelPlural()/groupLabelSingular() already normalize
// an unknown/unset value to DEFAULT_TASK_GROUP_LABEL.
//
// The duration field's copy (TPT9107) also follows the project's language, like the
// Notifications tab. The grouping noun has no Ukrainian forms in this SPA, so the Ukrainian
// copy names the group generically.
const SPRINT_DURATION_TEXT = {
  en: {
    label: singular => `${singular} duration (days)`,
    hint: singular => `Automatic dates give each ${singular.toLowerCase()} at least this many days, from 1 to 90. Changing this leaves existing dates alone.`,
    saved: (singular, days) => `${singular} duration set to ${days} ${days === 1 ? 'day' : 'days'}`,
  },
  uk: {
    label: () => 'Тривалість групи завдань (днів)',
    hint: () => 'Автоматичні дати дають кожній групі завдань щонайменше стільки днів, від 1 до 90. Зміна не торкається наявних дат.',
    saved: (_singular, days) => `Тривалість групи завдань: ${days} дн.`,
  },
};
const sprintDurationText = language => SPRINT_DURATION_TEXT[language] || SPRINT_DURATION_TEXT.en;

export function sprintsCardCaptions(value, language) {
  const plural = groupLabelPlural(value); // e.g. "Batches"
  const singular = groupLabelSingular(value); // e.g. "Batch"
  const lowerP = plural.toLowerCase();
  const lowerS = singular.toLowerCase();
  const duration = sprintDurationText(language);
  return {
    heading: plural,
    intro: `Show or hide ${lowerS} (task group) containers on the board. Hiding only affects display — ${lowerS} assignments are kept and restored when re-enabled.`,
    toggleLabel: `Show ${plural}`,
    toggleHint: `Board groups tasks into ${lowerP}; disable for a flat task list`,
    exampleHint: `Board group title bars will read “${singular} 3”.`,
    durationLabel: duration.label(singular),
    durationHint: duration.hint(singular),
  };
}

// ── Sprints toggle card (C1331) ──
// `sprints_enabled` arrives as TINYINT (0/1), not real bool — truthiness check, same as
// vcs_* flags above. Column default 1 → untouched project reads as on. Lives in own
// .settings-section (see settingsPage()'s Workflow pane) so Workflow-tab re-renders
// (rename/recolor/role/reorder — reRenderWorkflowTab()) never wipe it.
export function renderSprintsCard(project, canEdit) {
  const currentLabel = project.task_group_label || DEFAULT_TASK_GROUP_LABEL;
  const c = sprintsCardCaptions(currentLabel, project.language);
  const sortOrder = project.sprint_sort_order === 'desc' ? 'desc' : 'asc';
  return `<div class="card">
    <h3 id="sprints-card-heading">${esc(c.heading)}</h3>
    <p class="text-muted mb-2" id="sprints-card-intro">${esc(c.intro)}</p>
    <label class="vcs-suboption">
      <input type="checkbox" id="settings-sprints-enabled" ${project.sprints_enabled ? 'checked' : ''} ${canEdit ? '' : 'disabled'}>
      <span class="vcs-suboption-label" id="sprints-toggle-label">${esc(c.toggleLabel)}</span>
      <span class="vcs-suboption-hint text-muted" id="sprints-toggle-hint">${esc(c.toggleHint)}</span>
    </label>
    <div class="form-group" id="sprint-duration-group" style="margin-top:1rem;" ${project.sprints_enabled ? '' : 'hidden'}>
      <label class="form-label" for="settings-sprint-duration" id="sprint-duration-label">${esc(c.durationLabel)}</label>
      <input class="form-input" type="number" id="settings-sprint-duration" style="max-width:120px;" min="1" max="90" step="1" value="${esc(project.sprint_duration_days)}" aria-describedby="sprint-duration-error sprint-duration-hint" ${canEdit ? '' : 'disabled'}>
      <p class="settings-field-error" id="sprint-duration-error" role="alert" hidden></p>
      <p class="text-muted mb-2" id="sprint-duration-hint">${esc(c.durationHint)}</p>
    </div>
    <div class="form-group" id="task-group-label-group" style="max-width:280px;" ${project.sprints_enabled ? '' : 'hidden'}>
      <label class="form-label" for="settings-task-group-label">Group tasks into</label>
      <select class="form-select" id="settings-task-group-label" ${canEdit ? '' : 'disabled'}>
        ${TASK_GROUP_LABELS.map(l => `<option value="${esc(l)}" ${l === currentLabel ? 'selected' : ''}>${esc(groupLabelPlural(l))}</option>`).join('')}
      </select>
      <p class="text-muted mb-2" id="task-group-label-hint">${esc(c.exampleHint)}</p>
    </div>
    <div class="form-group" id="sprint-sort-order-group" style="max-width:320px;" ${project.sprints_enabled ? '' : 'hidden'}>
      <label class="form-label" for="settings-sprint-sort-order">Board order (Task App)</label>
      <select class="form-select" id="settings-sprint-sort-order" ${canEdit ? '' : 'disabled'}>
        <option value="asc" ${sortOrder === 'asc' ? 'selected' : ''}>Ascending (lowest sprint first)</option>
        <option value="desc" ${sortOrder === 'desc' ? 'selected' : ''}>Descending (highest sprint first)</option>
      </select>
      <p class="text-muted mb-2">Order the Task App board's sprint containers top to bottom. Ascending shows dependencies above the tasks that depend on them, with Load More Sprints at the top.</p>
    </div>
  </div>`;
}

// Live repaint of the Sprints card captions — called on the Group-tasks-into select's
// `change` handler BEFORE the PATCH round-trip (immediate flip, no reload), and again on
// PATCH failure to revert alongside the reverted select value. Uses `textContent`, not
// `innerHTML` — no esc() needed — and targets ids rather than rebuilding the card, since
// `#settings-sprints-enabled`/`#settings-task-group-label` are bound exactly once outside
// reRenderWorkflowTab()'s replaced subtree; rebuilding the card would silently drop both
// listeners.
function paintSprintsCardCaptions(main, value, language) {
  const c = sprintsCardCaptions(value, language);
  const set = (sel, text) => { const el = main.querySelector(sel); if (el) el.textContent = text; };
  set('#sprints-card-heading', c.heading);
  set('#sprints-card-intro', c.intro);
  set('#sprints-toggle-label', c.toggleLabel);
  set('#sprints-toggle-hint', c.toggleHint);
  set('#task-group-label-hint', c.exampleHint);
  set('#sprint-duration-label', c.durationLabel);
  set('#sprint-duration-hint', c.durationHint);
}

// ── Sprint duration field (TPT9107) ──
// One `change` of the field. Returns the saved project, or null when nothing was saved. The API
// owns the 1-90 rule: min/max only bound the spinner, and an empty field reads '' and goes out as
// 0, so every bad entry comes back as its 400. A 400 leaves the entry in the field, beside the
// API's message, to be corrected; going back to the saved value ends the error with no request.
// Any other failure says nothing about the entry, so the field reverts and the error goes on to
// the caller's toast, like the page's other autosave controls — a field left on an unsaved value
// would never fire `change` for that same value again, so it could not be retried.
export async function saveSprintDuration(client, projectId, savedDays, input, errorEl) {
  const showError = (message) => {
    errorEl.textContent = message;
    errorEl.hidden = !message;
    if (message) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  };
  const days = Number(input.value);
  if (days === savedDays) {
    input.value = savedDays;
    showError('');
    return null;
  }
  try {
    const res = await client.patch('/projects/' + projectId, { sprint_duration_days: days });
    input.value = res.project.sprint_duration_days;
    showError('');
    return res.project;
  } catch (err) {
    if (err.status === 400) {
      showError(err.message);
      return null;
    }
    input.value = savedDays;
    showError('');
    throw err;
  }
}

// When the field saves. Chrome fires `change` on every arrow-key step, auto-repeat included, so
// a held key would save and toast once per day stepped: steps wait for the key's release, or for
// the field losing focus, which is all that ends a hold if the key is let go somewhere else.
export function bindSprintDurationSaves(input, save) {
  const isStepKey = e => e.key === 'ArrowUp' || e.key === 'ArrowDown';
  let stepping = false;
  const finishStep = () => {
    if (!stepping) return;
    stepping = false;
    save();
  };
  input.addEventListener('keydown', (e) => {
    if (!isStepKey(e)) return;
    stepping = true;
    if (input.readOnly) e.preventDefault(); // a save is running; the key would scroll the page instead
  });
  input.addEventListener('keyup', (e) => { if (isStepKey(e)) finishStep(); });
  input.addEventListener('blur', finishStep);
  input.addEventListener('change', () => { if (!stepping) save(); });
}

// ── Memory tab (C1489) ──
// `kb_sync_as_you_go` arrives as TINYINT (0/1) off `GET /projects/:id`, not a real bool —
// truthiness check, same as sprints_enabled/vcs_* flags. Column default 1 → an untouched
// project reads as on (matches today's unconditional forced-sync-on-task-completion
// behavior — see ai/architecture/tt-api-projects.md § KB Sync As You Go, C1488). This tab
// only writes the flag; the Task App (C1490) is the actual consumer that gates its
// on-task-completion knowledge-base sync on it. Reuses the Version Control tab's
// `.vcs-suboption`/`.vcs-suboption-label`/`.vcs-suboption-hint` CSS, same precedent as
// renderSprintsCard() above — no new styles.
function renderMemoryTab(project, canEdit) {
  return `<div class="card">
    <h3>Memory</h3>
    <p class="text-muted mb-2">Control when this project's knowledge base (architecture docs, CLAUDE.md, AGENTS.md) syncs to the Task App.</p>
    <label class="vcs-suboption">
      <input type="checkbox" id="settings-kb-sync-as-you-go" ${project.kb_sync_as_you_go ? 'checked' : ''} ${canEdit ? '' : 'disabled'}>
      <span class="vcs-suboption-label">Sync as you go</span>
      <span class="vcs-suboption-hint text-muted">On: knowledge base sync is forced on every task completion. Off: sync only happens on Task App startup and via manual Project &rsaquo; Knowledge Base &rsaquo; Sync.</span>
    </label>
  </div>`;
}

// ── Objectives card (C1557) ──
// `use_objective_grouping` arrives as TINYINT (0/1) off `GET /projects/:id`, not a real
// bool — truthiness check, same as sprints_enabled/vcs_*/kb_sync_as_you_go. Column default 1
// → untouched project reads as on (matches today's behavior: an objective needing >1 task
// files a parent/objective task with subtasks under it — see
// ai/architecture/tt-api-projects.md § Objective Task Grouping, C1556). Own card, own
// .settings-section, sitting in the Sprints card's sibling slot rather than inside it — the
// Sprints card's heading/captions are all derived from the project's grouping noun
// (sprintsCardCaptions() → "Batches"/"Show Batches"), so an objective toggle would render
// under a heading that renames itself out from under it. This card only writes the flag —
// the objective-chat planner (parent-vs-single-task decision, C1558), web-created-task
// routing (C1559), and both board renderers hiding subtasks (C1460) are separate sibling
// tasks that read and gate on it. Reuses the Version Control/Memory tabs' .vcs-suboption/
// .vcs-suboption-label/.vcs-suboption-hint CSS — no new styles.
function renderObjectivesCard(project, canEdit) {
  return `<div class="card">
    <h3>Objectives</h3>
    <p class="text-muted mb-2">Control how multi-step objectives are broken into tasks.</p>
    <label class="vcs-suboption">
      <input type="checkbox" id="settings-use-objective-grouping" ${project.use_objective_grouping ? 'checked' : ''} ${canEdit ? '' : 'disabled'}>
      <span class="vcs-suboption-label">Use Objective Tasks Grouping</span>
      <span class="vcs-suboption-hint text-muted">On: an objective needing more than one task files a parent objective task with subtasks under it. Off: only individual tasks are created, no parent container.</span>
    </label>
  </div>`;
}

// ── Agent permissions card (TPT9063) ──
// `agent_permission_mode` arrives off `GET /projects/:id` as one of the five stored modes or
// null — "Not set", the legacy behaviour kept, not a sixth mode (see
// ai/architecture/tt-api-projects.md § Agent Permission Modes). Own card in the Workflow pane.
// The null placeholder is disabled, so the picker only ever
// submits a real mode — there is no way back to null from this UI. This card only writes the
// project default: task overrides keep their own value and running sessions are never touched;
// the Task App resolves the effective mode when a task starts. Exported as a test seam.
export function renderAgentPermissionMode(project, canEdit) {
  const current = project.agent_permission_mode || null;
  return `<div class="card">
    <h3>Agent permissions</h3>
    <p class="text-muted mb-2">Default permission mode for every task in this project that has no override of its own; applied when a task starts.</p>
    <div class="form-group" style="max-width:280px;">
      <label class="form-label" for="settings-agent-permission-mode-trigger">Permission mode</label>
      ${renderPermissionModeSelect(current, { id: 'settings-agent-permission-mode', disabled: !canEdit })}
      <p class="text-muted mb-2" id="agent-permission-mode-hint">${permissionModeHint(current)}</p>
    </div>
  </div>`;
}

// ── Project Workflow tab (C1182) ──
// `canEdit` gates status writes; callers use the statuses:manage capability.
export function renderWorkflowTab(statuses, canEdit, addColor) {
  const rows = [...(statuses || [])].sort((a, b) => a.display_order - b.display_order);

  const rowHtml = (s) => {
    const attrs = statusBadgeAttrs(s);
    const holdsRole = s.is_workflow_start || s.is_in_progress || s.is_workflow_complete || s.is_workflow_canceled;
    const deleteBlocked = holdsRole || s.task_count > 0;
    const deleteTitle = holdsRole
      ? 'Cannot delete — this status holds a workflow role'
      : (s.task_count > 0 ? `Cannot delete — ${s.task_count} task(s) use this status` : 'Delete status');

    if (!canEdit) {
      return `<div class="wf-row" data-id="${s.id}">
        <span class="wf-chip ${attrs.className}" style="${attrs.style}">
          <span class="wf-chip-label">${esc(statusLabel(s.name))}</span>
        </span>
        <span class="wf-count">${s.task_count}</span>
        <span class="wf-radio">${s.is_workflow_start ? '●' : ''}</span>
        <span class="wf-radio">${s.is_in_progress ? '●' : ''}</span>
        <span class="wf-radio">${s.is_workflow_complete ? '●' : ''}</span>
        <span class="wf-radio">${s.is_workflow_canceled ? '●' : ''}</span>
      </div>`;
    }

    // Chip shows the translated label (statusLabel); clicking it swaps in the raw-slug
    // input for editing (bindWorkflowRowEvents' 'click' handler below) — rename still
    // PATCHes the literal slug typed there (C1190 decision: transliteration applies to the
    // add form only, not rename). Delete lives as its own column at the end of the row
    // (C1191), not inside the chip — column order: drag handle, chip, count, 4 role
    // radios, delete. Radio titles/aria-labels are board-stage names (C1192), not the raw
    // is_workflow_* role-flag names — see the WF_STAGE_LABELS header row below, which these
    // must match column-for-column.
    const label = esc(statusLabel(s.name));
    return `<div class="wf-row" data-id="${s.id}">
      <span class="wf-drag-handle" title="Drag to reorder">&#10495;</span>
      <span class="wf-chip ${attrs.className}" style="${attrs.style}">
        <span class="wf-chip-label" data-id="${s.id}" title="Click to rename">${label}</span>
        <input type="text" class="wf-chip-name" hidden maxlength="64" value="${esc(s.name)}" data-id="${s.id}" data-prev="${esc(s.name)}">
        ${colorTriggerHtml(s.color, { 'data-id': s.id })}
      </span>
      <span class="wf-count">${s.task_count}</span>
      <label class="wf-radio" title="To Do">
        <input type="radio" name="wf-role-start" data-role="is_workflow_start" data-id="${s.id}" aria-label="To Do: ${label}" ${s.is_workflow_start ? 'checked' : ''}>
      </label>
      <label class="wf-radio" title="In Progress">
        <input type="radio" name="wf-role-progress" data-role="is_in_progress" data-id="${s.id}" aria-label="In Progress: ${label}" ${s.is_in_progress ? 'checked' : ''}>
      </label>
      <label class="wf-radio" title="Completed">
        <input type="radio" name="wf-role-complete" data-role="is_workflow_complete" data-id="${s.id}" aria-label="Completed: ${label}" ${s.is_workflow_complete ? 'checked' : ''}>
      </label>
      <label class="wf-radio" title="Canceled">
        <input type="radio" name="wf-role-canceled" data-role="is_workflow_canceled" data-id="${s.id}" aria-label="Canceled: ${label}" ${s.is_workflow_canceled ? 'checked' : ''}>
      </label>
      <button type="button" class="wf-row-x" data-id="${s.id}" ${deleteBlocked ? 'disabled' : ''} title="${esc(deleteTitle)}">&times;</button>
    </div>`;
  };

  // Stage header row (C1192) — a SIBLING of #wf-table, not a row inside it: the drag-reorder
  // code (bindWorkflowRowEvents' mousemove/mouseup below) queries `#wf-table`'s `.wf-row`
  // children directly and does index math against them, so a `.wf-row`-classed header would
  // become a drag target and shift every reorder index. `aria-hidden` because each radio
  // already carries its own `aria-label` above — a visual header a screen reader also reads
  // out would just duplicate that.
  const headHtml = `<div class="wf-head" aria-hidden="true">
    <span class="wf-head-spacer"></span>
    <span class="wf-count" title="Tasks with this status">#</span>
    <span class="wf-head-cell">To Do</span>
    <span class="wf-head-cell">In Progress</span>
    <span class="wf-head-cell">Completed</span>
    <span class="wf-head-cell">Canceled</span>
    ${canEdit ? '<span class="wf-head-x"></span>' : ''}
  </div>`;

  return `<div class="card">
    <h3>Project Workflow</h3>
    <p class="text-muted mb-2">Add, rename, recolor, delete, and reorder this project's task statuses. Each status is a board column; the four stage columns below mark which status means To Do, In Progress, Completed, and Canceled — agents apply those stages instead of literal status names.</p>
    ${headHtml}
    <div class="wf-table" id="wf-table">${rows.map(rowHtml).join('')}</div>
    ${canEdit ? `
    <form class="wf-add-form" id="wf-add-form">
      <input type="text" class="form-input form-input-sm" id="wf-add-name" placeholder="New status name" maxlength="64" required>
      ${colorTriggerHtml(addColor, { id: 'wf-add-color-trigger' })}
      <button type="submit" class="btn btn-primary btn-sm" id="wf-add-btn">Add</button>
    </form>` : ''}
  </div>`;
}

// Names the Workflow-table control that has keyboard focus by the attributes renderWorkflowTab()
// gives its copy in the next render (TPT9174): a role radio by status id and role, a row's color
// trigger or delete button by status id, the add form's controls by id. The rename input, hidden
// again after every render, has no copy to focus.
export function workflowFocusSelector(el) {
  const sid = el.dataset.id;
  if (sid && el.type === 'radio' && el.dataset.role) return `input[type="radio"][data-role="${el.dataset.role}"][data-id="${sid}"]`;
  if (sid && el.classList.contains('wf-color-trigger')) return `.wf-color-trigger[data-id="${sid}"]`;
  if (sid && el.classList.contains('wf-row-x')) return `.wf-row-x[data-id="${sid}"]`;
  return el.id ? `#${el.id}` : null;
}

// The Workflow rows after a drag moves status `statusId` to row `newIndex`, or null when it lands
// back in place. Copies with new display_order values, never the rows passed in: those are the
// rows loadStatuses() caches, and a failed save puts them back on screen in their saved order
// (TPT9177).
export function reorderedStatuses(statuses, statusId, newIndex) {
  const ordered = [...statuses].sort((a, b) => a.display_order - b.display_order);
  const prevIds = ordered.map(s => s.id);
  const from = ordered.findIndex(s => String(s.id) === String(statusId));
  if (from === -1) return null;
  const [moved] = ordered.splice(from, 1);
  ordered.splice(Math.min(newIndex, ordered.length), 0, moved);
  if (ordered.every((s, i) => s.id === prevIds[i])) return null; // dropped back in place
  return ordered.map((s, i) => ({ ...s, display_order: i }));
}

// The row a Workflow drag drops status `statusId` on, reorderedStatuses()'s `newIndex`: how many
// other status rows sit in front of the placeholder in `table` (TPT9184). The dragged row stays in
// the table while the drag runs, only hidden, so counting every child put a downward drop one row
// too low. A re-render during the drag (a save landing, such as the previous drop's PUT) leaves an
// unhidden copy of the dragged row in the new table, which mousemove moves the placeholder into, so
// rows are skipped by status id as well as by `.wf-row-hidden`. -1 when the placeholder is not in
// `table`: the table is gone, or was re-rendered after the last mousemove, and nothing is saved.
export function workflowDropIndex(table, placeholder, statusId) {
  if (!table || !placeholder || placeholder.parentNode !== table) return -1;
  return dropIndex(table, placeholder, {
    selector: '.wf-row',
    hiddenClass: 'wf-row-hidden',
    filter: (el) => el.dataset.id !== String(statusId),
  });
}

// Points Workflow drag `drag` at its row as `table` has it now (TPT9189). reRenderWorkflowTab() replaces
// the table, so a save landing while the drag is pressed or under way (such as the previous drop's PUT)
// leaves `drag.el` in the old, detached table: it measures 0 wide, so the clone is built 0 wide, and its
// re-rendered copy shows beside the clone. `drag.el` becomes that copy, found by status id, hidden like
// the original once the drag is under way. False when `table` has no row for the status any more.
export function relinkWorkflowDrag(table, drag) {
  if (drag.el.isConnected) return true;
  const row = table ? [...table.querySelectorAll('.wf-row')].find(el => el.dataset.id === String(drag.id)) : null;
  if (!row) return false;
  drag.el = row;
  if (drag.moved) row.classList.add('wf-row-hidden');
  return true;
}

export async function settingsPage(params) {
  // Read before the first await: once it changes, the user navigated on and this mount stops
  // after its next await (see router.js getNavigationId()).
  const navId = getNavigationId();
  const superseded = () => getNavigationId() !== navId;
  if (!(await checkAuth()) || superseded()) return;
  const id = params.id;

  // Workflow-tab drag reorder document-level listeners (C1182) — declared here so the
  // page's teardown (bottom of this function) can always detach them, even though
  // they're only assigned/attached inside the `canManageSettings` branch further down.
  let _wfOnMouseDown = null, _wfOnMouseMove = null, _wfOnMouseUp = null;
  // Owner token of the latest modal this page opened (TPT647) — its teardown closes only that one,
  // so a late teardown leaves a modal the next page opened alone.
  let modalToken = null;
  // Add-status form's staged color pick (C1189) — survives reRenderWorkflowTab() since
  // that rebuilds the form's DOM from scratch; must live outside it.
  let wfAddColor = WORKFLOW_COLOR_TOKENS[0];
  // Set (below, inside the Workflow-tab `canManageSettings` block) to reRenderWorkflowTab — a plain
  // `function` declared inside a block is block-scoped in this ESM/strict-mode file, so the
  // Language change handler (a separate, earlier `canManageSettings` block) can't call it by name
  // directly. Re-labels the Workflow tab's chips live after a language switch (C1190).
  let cleanupPermissionModes = null;
  let cleanupWebhooks = null;
  let refreshWorkflowLabels = null;

  initLayout(getUser());
  updateLayoutContext({ projectId: id, projectName: null, activeView: 'settings', viewLabel: 'Settings' });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header">
      <h2>Project Settings</h2>
    </div>
    <div class="empty"><span class="spinner"></span></div>`;

  let project, members, statuses, notificationLayers;
  try {
    const [projRes, membersRes, statusesRes, notifRes] = await Promise.all([
      api.get('/projects/' + id),
      api.get('/projects/' + id + '/members'),
      loadStatuses(id),
      // Own leg so a notification-settings failure never blocks the whole page load
      // (C1350) — falls back to the same defaults GET returns for a missing row.
      api.get('/projects/' + id + '/notification-settings').catch(() => null),
    ]);
    if (superseded()) return;
    project = projectWithPermissions(projRes);
    members = membersRes.members;
    statuses = statusesRes || [];
    notificationLayers = notifRes && notifRes.personal_settings ? notifRes : null;
    setStatusLocale(project.language || 'en'); // C1190 — chip labels follow this project's language
  } catch (err) {
    if (superseded()) return;
    content.innerHTML = `
      <div class="page-header">
        <h2>Project Settings</h2>
      </div>
      <div class="empty text-muted">Failed to load project</div>`;
    return;
  }

  updateLayoutContext({ projectId: id, projectName: project.name, activeView: 'settings', viewLabel: 'Settings' });

  const canManageSettings = can(project, getUser(), 'settings:manage');
  const canManageMembers = can(project, getUser(), 'member:manage');
  const canManageStatuses = can(project, getUser(), 'statuses:manage');
  const canDeleteProject = can(project, getUser(), 'project:delete');
  const canManageWebhooks = can(project, getUser(), 'project:admin');
  const main = content;
  const myRole = effectiveRole(project, getUser());
  const { tabs, defaultTab } = settingsTabsFor({ canManageSettings, canManageMembers, canDeleteProject, canManageWebhooks });
  const initialTab = settingsTabFromQuery(tabs, defaultTab, getQueryParams());
  const paneHidden = tab => (tab === initialTab ? '' : 'hidden');
  main.innerHTML = `
    <div class="page-header">
      <h2>Project Settings — <span class="project-name-heading">${esc(project.name)}</span></h2>
      ${myRole ? `<span class="role-pill" title="${esc(ROLE_DESCRIPTIONS[myRole] || 'Full access to every project.')}">Your role ${roleBadgeHtml(myRole)}</span>` : ''}
    </div>

    <div class="settings-tabs" id="settings-tabs">
      ${tabs.map(tab => `<button class="settings-tab${tab === initialTab ? ' active' : ''}" type="button" data-tab="${tab}">${tab === 'webhooks' ? webhookText(project.language, 'title') : SETTINGS_TAB_LABELS[tab]}</button>`).join('')}
    </div>

    ${canManageSettings ? `
    <div class="settings-pane" data-tab="general" ${paneHidden('general')}>
      <div class="settings-section">
        <div class="card">
          <h3>Project Info</h3>
          <form id="project-info-form">
            <div class="form-group">
              <label class="form-label" for="settings-name">Name</label>
              <input class="form-input" id="settings-name" value="${esc(project.name)}" maxlength="255" required>
            </div>
            <div class="form-group">
              <label class="form-label" for="settings-desc">Description</label>
              <textarea class="form-textarea" id="settings-desc" placeholder="Optional description">${esc(project.description || '')}</textarea>
            </div>
            <button type="submit" class="btn btn-primary" id="save-project-btn">Save</button>
          </form>
        </div>
      </div>

      <div class="settings-section">
        <div class="card">
          <h3>Language</h3>
          <p class="text-muted mb-2">Language used by the UI, objective chat, and task agents for this project.</p>
          <div class="form-group" style="max-width:280px;">
            <select class="form-select" id="settings-language">
              <option value="en">English</option>
              <option value="uk">Українська (Ukrainian)</option>
              <option value="zh">简体中文 (Simplified Chinese)</option>
              <option value="es">Español (Spanish)</option>
            </select>
          </div>
        </div>
      </div>

    </div>` : ''}

    <div class="settings-pane" data-tab="members" ${paneHidden('members')}>
      <div class="settings-section">
        <div class="card">
          <h3>Members</h3>
          ${canManageMembers ? '' : '<p class="text-muted mb-2">Only the owner and project admins can add members or change roles.</p>'}
          <div id="members-list">${renderMembersTable(members, canManageMembers, getUser()?.id)}</div>
          ${canManageMembers ? `
          <form id="add-member-form" class="add-member-form">
            <div class="form-group add-member-email">
              <label class="form-label" for="member-email">Add member</label>
              <input class="form-input" id="member-email" type="email" placeholder="user@example.com" required>
            </div>
            <div class="form-group">
              <label class="form-label" for="member-role">Role</label>
              <select class="form-select" id="member-role">
                ${memberRoleOptions()}
              </select>
            </div>
            <button type="submit" class="btn btn-primary" id="add-member-btn">Add</button>
          </form>` : ''}
          ${renderRoleLegend()}
        </div>
      </div>
    </div>

    ${canManageSettings ? `<div class="settings-pane" data-tab="workflow" ${paneHidden('workflow')}>
      <div class="settings-section">
        ${renderSprintsCard(project, canManageSettings)}
      </div>
      <div class="settings-section">
        ${renderObjectivesCard(project, canManageSettings)}
      </div>
      <div class="settings-section">
        ${renderAgentPermissionMode(project, canManageSettings)}
      </div>
      <div class="settings-section" id="wf-section">
        ${renderWorkflowTab(statuses, canManageStatuses, wfAddColor)}
      </div>
    </div>` : ''}

    ${canManageSettings ? `
    <div class="settings-pane" data-tab="version-control" ${paneHidden('version-control')}>
      <div class="settings-section">
        ${renderVersionControlTab(project)}
      </div>
    </div>` : ''}

    ${canManageSettings ? `
    <div class="settings-pane" data-tab="memory" ${paneHidden('memory')}>
      <div class="settings-section">
        ${renderMemoryTab(project, canManageSettings)}
      </div>
    </div>

    <div class="settings-pane" data-tab="appearance" ${paneHidden('appearance')}>
      <div class="settings-section">
        <div class="card">
          <h3>Appearance</h3>
          <p class="text-muted mb-2">Color scheme this project uses on its own pages. Non-project pages (Dashboard, All Tasks, Security) always follow the light/dark switch in the navbar instead.</p>
          <div id="settings-theme-options">
            ${renderColorSchemeOptions(project.color_scheme || DEFAULT_COLOR_SCHEME, canManageSettings)}
          </div>
        </div>
      </div>
    </div>` : ''}

    <div class="settings-pane" data-tab="notifications" ${paneHidden('notifications')}>
      <div class="settings-section" id="settings-notifications-section">
        ${buildNotificationsTab(notificationLayers, project.language || 'en', canManageSettings)}
      </div>
    </div>

    ${canManageWebhooks ? `<div class="settings-pane" data-tab="webhooks" ${paneHidden('webhooks')}>
      <div class="settings-section" id="settings-webhooks-section"></div></div>` : ''}

    ${canDeleteProject ? `
    <div class="settings-pane" data-tab="danger" ${paneHidden('danger')}>
      <div class="settings-section">
        <div class="card danger-zone">
          <h3>Danger Zone</h3>
          <p class="text-muted mb-2">Permanently delete this project and all its tasks.</p>
          <button class="btn btn-danger" id="delete-project-btn">Delete Project</button>
        </div>
      </div>
    </div>` : ''}`;

  // ── Tab switching ──
  main.querySelector('.settings-tab.active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  if (canManageWebhooks) cleanupWebhooks = mountWebhooks(main.querySelector('#settings-webhooks-section'), api, id, project.language);
  main.querySelectorAll('.settings-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      main.querySelectorAll('.settings-tab').forEach(b => b.classList.toggle('active', b === btn));
      main.querySelectorAll('.settings-pane').forEach(pane => {
        pane.hidden = pane.dataset.tab !== btn.dataset.tab;
      });
      const params = getQueryParams();
      params.set('tab', btn.dataset.tab);
      setQueryParams(params);
    });
  });

  // ── Appearance (C1303) — writes the PROJECT's color_scheme, not the workspace theme.
  // settings:manage gates both radios and their write handler.
  let prevColorScheme = project.color_scheme || DEFAULT_COLOR_SCHEME;
  main.querySelectorAll('input[name="settings-theme"]').forEach(input => {
    input.addEventListener('change', async (e) => {
      if (!canManageSettings || !e.target.checked) return;
      const colorScheme = e.target.value;
      try {
        const res = await api.patch('/projects/' + id, { color_scheme: colorScheme });
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        prevColorScheme = colorScheme;
        showToast('Color scheme updated', 'success');
        // Single repaint path (ui.js's applyThemeForContext, via layout.js). Passing
        // colorScheme also refreshes layout.js's projectCache/sidebar's cachedProjects,
        // so the next navigation doesn't repaint the now-stale old value.
        updateLayoutContext({ projectId: id, projectName: project.name, activeView: 'settings', viewLabel: 'Settings', colorScheme });
      } catch (err) {
        const prevInput = main.querySelector(`input[name="settings-theme"][value="${CSS.escape(prevColorScheme)}"]`);
        if (prevInput) prevInput.checked = true;
        showToast(err.message || 'Failed to save color scheme', 'error');
      }
    });
  });

  // ── Memory tab — Sync as you go (C1489) — writes `kb_sync_as_you_go`. Checkbox renders
  // settings:manage gates the checkbox and handler. Autosaves like the Sprints toggle.
  const kbSyncCb = main.querySelector('#settings-kb-sync-as-you-go');
  if (kbSyncCb && canManageSettings) {
    kbSyncCb.addEventListener('change', async () => {
      const enabled = kbSyncCb.checked; // real boolean — API rejects 0/1/'true'
      const enable = disableForSave(kbSyncCb); // enable() also gives back keyboard focus
      try {
        const res = await api.patch('/projects/' + id, { kb_sync_as_you_go: enabled });
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        showToast(enabled ? 'Sync as you go enabled' : 'Sync as you go disabled', 'success');
      } catch (err) {
        kbSyncCb.checked = !enabled;
        showToast(err.message || 'Failed to save', 'error');
      }
      enable();
    });
  }

  // Autosave each layer through its own endpoint. A successful PATCH rebuilds controls
  // from raw source rows, so inherited selections and effective labels remain accurate.
  let notificationLanguage = project.language || 'en';
  const notificationSection = main.querySelector('#settings-notifications-section');
  function renderNotificationSettings() {
    notificationSection.innerHTML = buildNotificationsTab(notificationLayers, notificationLanguage, canManageSettings);
    bindNotificationSettings();
  }
  function bindNotificationSettings() {
    notificationSection.querySelectorAll('[data-notification-field]').forEach(select => {
      select.addEventListener('change', async () => {
        const previous = select.dataset.notificationScope === 'personal'
          ? (notificationLayers.personal_settings[select.dataset.notificationField] ?? 'inherit')
          : notificationLayers.project_settings[select.dataset.notificationField];
        const value = select.value === 'inherit' ? null : select.value;
        const controls = [...notificationSection.querySelectorAll('[data-notification-field]')];
        const enable = disableForSave(controls);
        try {
          notificationLayers = await saveNotificationChoice(api, id, notificationLayers,
            select.dataset.notificationScope, select.dataset.notificationField, value);
          renderNotificationSettings();
          showToast('Notification settings updated', 'success');
        } catch (err) {
          select.value = previous;
          showToast(err.message || 'Failed to save notification settings', 'error');
        }
        // After a success the rerender has replaced every select: keyboard focus goes to the
        // new one with the focused select's id.
        enable();
      });
    });
  }
  bindNotificationSettings();

  // ── Project info save ──
  if (canManageSettings) {
    main.querySelector('#project-info-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = main.querySelector('#save-project-btn');
      const name = main.querySelector('#settings-name').value.trim();
      const description = main.querySelector('#settings-desc').value.trim();
      if (!name) return;

      const enable = disableForSave(btn); // enable() also gives back keyboard focus
      btn.textContent = 'Saving...';
      try {
        const res = await api.patch('/projects/' + id, { name, description: description || null });
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        showToast('Project updated', 'success');
      } catch (err) {
        showToast(err.message || 'Failed to save', 'error');
      }
      btn.textContent = 'Save';
      enable();
    });

    // ── Language ──
    const langSelect = main.querySelector('#settings-language');
    langSelect.value = project.language || 'en';
    let prevLanguage = langSelect.value;
    langSelect.addEventListener('change', async () => {
      const enable = disableForSave(langSelect); // enable() also gives back keyboard focus
      try {
        const res = await api.patch('/projects/' + id, { language: langSelect.value });
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        prevLanguage = langSelect.value;
        setStatusLocale(langSelect.value); // C1190 — re-label Workflow tab chips live
        notificationLanguage = langSelect.value;
        if (canManageWebhooks) {
          cleanupWebhooks?.();
          cleanupWebhooks = mountWebhooks(main.querySelector('#settings-webhooks-section'), api, id, langSelect.value);
          main.querySelector('.settings-tab[data-tab="webhooks"]').textContent = webhookText(langSelect.value, 'title');
        }
        renderNotificationSettings();
        if (refreshWorkflowLabels) refreshWorkflowLabels();
        paintSprintsCardCaptions(main, project.task_group_label, langSelect.value); // TPT9107 — duration copy
        showToast('Language updated', 'success');
      } catch (err) {
        langSelect.value = prevLanguage;
        showToast(err.message || 'Failed to save', 'error');
      }
      enable();
    });

    // ── Agent permissions (TPT9063) — autosave, same idiom as the Language select above and
    // the Objectives toggle / Group-tasks-into select in the Workflow block below. The hint is
    // repainted via textContent (fixed strings, no esc() needed), never by rebuilding the card.
    cleanupPermissionModes = bindPermissionModeSelects(main);
    const permissionModeSelect = main.querySelector('#settings-agent-permission-mode');
    const permissionModeHintEl = main.querySelector('#agent-permission-mode-hint');
    let prevPermissionMode = permissionModeSelect.value; // '' while the "Not set" placeholder shows
    permissionModeSelect.addEventListener('change', async () => {
      const value = permissionModeSelect.value; // always one of the five — the placeholder is disabled
      permissionModeSelect.disabled = true;
      syncPermissionModeSelect(permissionModeSelect);
      try {
        const res = await api.patch('/projects/' + id, { agent_permission_mode: value });
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — the next drawer open / page visit re-fetches
        prevPermissionMode = value;
        permissionModeHintEl.textContent = permissionModeHint(value);
        showToast(`Agent permission mode set to "${permissionModeLabel(value)}"`, 'success');
      } catch (err) {
        permissionModeSelect.value = prevPermissionMode; // '' re-selects the hidden placeholder
        permissionModeHintEl.textContent = permissionModeHint(prevPermissionMode || null);
        showToast(err.message || 'Failed to save', 'error');
      }
      permissionModeSelect.disabled = false;
      syncPermissionModeSelect(permissionModeSelect);
    });

    // ── Version Control (C1214) ──
    const vcsForm = main.querySelector('#vcs-form');
    const vcsGitOptions = main.querySelector('#vcs-git-options');
    // Toggle the suboption checkboxes' visibility only — their `checked` state is left
    // alone while hidden, so a git → svn → git flip in the same session restores the
    // previous ticks without a round-trip. Mirrors the server's own "dormant, not
    // force-reset" flag semantics (tt-api-projects.md § Version Control Settings).
    vcsForm.addEventListener('change', (e) => {
      if (!e.target.matches('input[name="vcs-type"]')) return;
      vcsGitOptions.hidden = e.target.value !== 'git';
    });

    vcsForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = main.querySelector('#save-vcs-btn');
      const vcsType = vcsForm.querySelector('input[name="vcs-type"]:checked').value;
      // buildVcsPatchBody() (lib/vcs-form.js) omits the three git suboptions entirely
      // unless git is selected — sending `false` for a non-git project would zero out
      // the stored flags, breaking the documented "dormant, restored on switching back"
      // behavior (C1213), and the API rejects a `true` value outright when vcs_type
      // isn't 'git'.
      const flagState = {};
      VCS_GIT_FLAGS.forEach(f => {
        flagState[f.field] = vcsForm.querySelector(`input[data-field="${f.field}"]`).checked;
      });
      const body = buildVcsPatchBody(vcsType, flagState);

      const enable = disableForSave(btn); // enable() also gives back keyboard focus
      btn.textContent = 'Saving...';
      try {
        const res = await api.patch('/projects/' + id, body);
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        showToast('Version control settings updated', 'success');
      } catch (err) {
        showToast(err.message || 'Failed to save', 'error');
      }
      btn.textContent = 'Save';
      enable();
    });
  }

  // ── Refresh members helper ──
  async function refreshMembers() {
    try {
      const res = await api.get('/projects/' + id + '/members');
      members = res.members;
      main.querySelector('#members-list').innerHTML = renderMembersTable(members, canManageMembers, getUser()?.id);
      bindRemoveButtons();
      bindRolePickers();
    } catch (err) {
      showToast('Failed to refresh members', 'error');
    }
  }

  // ── Remove member ──
  function bindRemoveButtons() {
    if (!canManageMembers) return;
    main.querySelectorAll('.remove-member-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const userId = btn.dataset.userId;
        const email = btn.dataset.email || '';
        const name = btn.dataset.name;
        const isPending = userId === '0' && email;
        modalToken = openModal('Remove Member', `
          <p>Remove <strong>${esc(name)}</strong> from this project?</p>
          <div class="modal-footer">
            <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
            <button class="btn btn-danger" id="confirm-remove-btn">Remove</button>
          </div>`, {
          onMount(overlay) {
            overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);
            overlay.querySelector('#confirm-remove-btn').addEventListener('click', async () => {
              const cb = overlay.querySelector('#confirm-remove-btn');
              const enable = disableForSave(cb); // enable() also gives back keyboard focus
              cb.textContent = 'Removing...';
              try {
                const path = isPending
                  ? '/projects/' + id + '/members/0?email=' + encodeURIComponent(email)
                  : '/projects/' + id + '/members/' + userId;
                await api.delete(path);
                closeModal(); // gives keyboard focus back to this member's Remove button
                const hadFocus = document.activeElement === btn;
                showToast(isPending ? 'Invitation removed' : 'Member removed', 'success');
                await refreshMembers();
                // The refreshed roster has no row for the removed member, so the focused Remove
                // button went with it: the Add member field takes focus instead (TPT9176).
                if (hadFocus && document.activeElement === document.body) main.querySelector('#member-email')?.focus();
              } catch (err) {
                cb.textContent = 'Remove';
                enable();
                showToast(err.message || 'Failed to remove', 'error');
              }
            });
          }
        });
      });
    });
  }
  bindRemoveButtons();

  function bindRolePickers() {
    if (!canManageMembers) return;
    main.querySelectorAll('.member-role-select').forEach(select => {
      const userId = select.dataset.userId;
      const isSelf = userId !== '0' && String(getUser()?.id) === userId;

      async function saveRole() {
        const previous = select.dataset.role;
        const enable = disableForSave(select);
        try {
          const suffix = userId === '0' ? '?email=' + encodeURIComponent(select.dataset.email) : '';
          await api.patch(`/projects/${id}/members/${userId}${suffix}`, { role: select.value });
          select.dataset.role = select.value;
          invalidateProject(id);
          showToast('Member role updated', 'success');
          if (isSelf) {
            // Reload so self-demotion removes manager controls and document listeners.
            window.location.reload();
            return;
          }
          await refreshMembers();
        } catch (err) {
          select.value = previous;
          showToast(err.message || 'Failed to update member role', 'error');
        } finally {
          // A refreshed roster has replaced this picker: keyboard focus goes to the new one
          // (same member, same id).
          enable();
        }
      }

      select.addEventListener('change', () => {
        if (!isSelf) { saveRole(); return; }
        // Changing your own role can remove your access to this very page — confirm
        // first; closing the dialog any other way puts the picker back.
        let confirmed = false;
        modalToken = openModal('Change your own role?', `
          <p>You are changing your own role from <strong>${esc(roleLabel(select.dataset.role))}</strong> to <strong>${esc(roleLabel(select.value))}</strong>.</p>
          <p class="text-muted">${esc(ROLE_DESCRIPTIONS[select.value] || '')} You may lose access to project settings and will not be able to undo this yourself.</p>
          <div class="modal-footer">
            <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
            <button class="btn btn-danger" id="confirm-self-role-btn">Change my role</button>
          </div>`, {
          onMount(overlay) {
            overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);
            overlay.querySelector('#confirm-self-role-btn').addEventListener('click', () => {
              confirmed = true;
              closeModal();
              saveRole();
            });
          },
          onClose() {
            if (!confirmed) select.value = select.dataset.role;
          },
        });
      });
    });
  }
  bindRolePickers();

  // ── Add member ──
  if (canManageMembers) {
    main.querySelector('#add-member-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const emailInput = main.querySelector('#member-email');
      const roleSelect = main.querySelector('#member-role');
      const btn = main.querySelector('#add-member-btn');
      const email = emailInput.value.trim();
      if (!email) return;

      const enable = disableForSave(btn); // enable() also gives back keyboard focus
      btn.textContent = 'Adding...';
      try {
        const res = await api.post('/projects/' + id + '/members', {
          email,
          role: roleSelect.value,
        });
        showToast(res && res.invite ? 'Invitation sent' : 'Member added', 'success');
        emailInput.value = '';
        refreshMembers();
      } catch (err) {
        showToast(err.message || 'Failed to add member', 'error');
      }
      btn.textContent = 'Add';
      enable();
    });
  }

  // ── Delete project ──
  if (canDeleteProject) {
    main.querySelector('#delete-project-btn').addEventListener('click', () => {
      const projName = project.name;
      modalToken = openModal('Delete Project', `
        <p>This will permanently delete <strong>${esc(projName)}</strong> and all its tasks.</p>
        <div class="form-group mt-2">
          <label class="form-label" for="confirm-name">Type the project name to confirm</label>
          <input class="form-input" id="confirm-name" placeholder="${esc(projName)}" autocomplete="off">
        </div>
        <div class="modal-footer">
          <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
          <button class="btn btn-danger" id="confirm-delete-btn" disabled>Delete Project</button>
        </div>`, {
        onMount(overlay) {
          const input = overlay.querySelector('#confirm-name');
          const delBtn = overlay.querySelector('#confirm-delete-btn');
          overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);

          input.addEventListener('input', () => {
            delBtn.disabled = input.value !== projName;
          });

          delBtn.addEventListener('click', async () => {
            const enable = disableForSave(delBtn); // enable() also gives back keyboard focus
            delBtn.textContent = 'Deleting...';
            try {
              await api.delete('/projects/' + id);
              closeModal();
              showToast('Project deleted', 'success');
              refreshProjectList();
              navigate('/');
            } catch (err) {
              delBtn.textContent = 'Delete Project';
              enable();
              showToast(err.message || 'Failed to delete', 'error');
            }
          });
        }
      });
    });
  }

  // ── Workflow tab (C1182) ──
  if (canManageSettings) {
    // The row drag in progress (drag reorder, below) — declared ahead of reRenderWorkflowTab(), which
    // points it at the rows it renders (TPT9189).
    let drag = null;
    function reRenderWorkflowTab() {
      // #wf-section, not the pane's first .settings-section — the Sprints card (C1331)
      // added its own sibling .settings-section ahead of this one, so a bare descendant
      // selector would grab that instead and every rename/recolor/role/reorder here
      // would clobber the Sprints checkbox with the status table markup.
      const pane = main.querySelector('#wf-section');
      // Replacing the markup removes the control that has keyboard focus; restoreFocus() gives
      // focus to its copy in the new markup (TPT9174).
      const restoreFocus = noteFocusBeforeRender(pane, workflowFocusSelector);
      if (pane) pane.innerHTML = renderWorkflowTab(statuses, canManageStatuses, wfAddColor);
      bindWorkflowRowEvents();
      bindWorkflowAddForm();
      restoreFocus();
      // A row drag pressed or under way still holds a row this render replaced.
      syncWorkflowDrag();
    }
    refreshWorkflowLabels = reRenderWorkflowTab; // see declaration above (C1190)

    // Rename/color/role/delete — safe to rebind on every re-render: `#wf-table` is a
    // fresh element each time (reRenderWorkflowTab replaces the pane's innerHTML), so
    // whatever was bound to the old node dies with it. Drag (below) is different —
    // it's bound to `document`, so it's wired exactly once, not from here.
    function bindWorkflowRowEvents() {
      const table = main.querySelector('#wf-table');
      if (!table) return;

      // Label -> edit swap (C1190): the chip shows the translated statusLabel(); clicking
      // it reveals the sibling .wf-chip-name input (raw slug) for rename, focused+selected.
      table.addEventListener('click', (e) => {
        const label = e.target.closest('.wf-chip-label');
        if (!label || !label.dataset.id) return; // read-only rows' label has no data-id
        const chip = label.closest('.wf-chip');
        const input = chip && chip.querySelector('.wf-chip-name');
        if (!input) return;
        label.hidden = true;
        input.hidden = false;
        input.focus();
        input.select();
      });

      table.addEventListener('focusout', async (e) => {
        const input = e.target.closest('.wf-chip-name');
        if (!input) return;
        const chip = input.closest('.wf-chip');
        const label = chip && chip.querySelector('.wf-chip-label');
        const sid = input.dataset.id;
        const prev = input.dataset.prev;
        const next = input.value.trim();
        if (!next || next === prev) {
          // No real change — just swap back to the label view, no PATCH/re-render needed.
          input.value = prev;
          input.hidden = true;
          if (label) label.hidden = false;
          return;
        }
        input.disabled = true;
        try {
          await api.patch(`/projects/${id}/statuses/${sid}`, { name: next });
          invalidateStatuses(id);
          statuses = await loadStatuses(id);
          showToast(`Renamed "${prev}" to "${next}"`, 'success');
        } catch (err) {
          showToast(err.message || 'Failed to rename status', 'error');
        }
        reRenderWorkflowTab();
      });
      table.addEventListener('keydown', (e) => {
        const input = e.target.closest('.wf-chip-name');
        if (!input) return;
        if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
        else if (e.key === 'Escape') { input.value = input.dataset.prev; input.blur(); }
      });

      // Role radios (delegated 'change') — color recolor moved to the swatch-picker click
      // branch below (C1189), it's a <button> not a <select> now so 'change' never fires.
      table.addEventListener('change', async (e) => {
        const radio = e.target.closest('input[type="radio"][data-role]');
        if (radio) {
          const sid = radio.dataset.id;
          const role = radio.dataset.role;
          try {
            await api.patch(`/projects/${id}/statuses/${sid}`, { [role]: true });
            invalidateStatuses(id);
            statuses = await loadStatuses(id);
          } catch (err) {
            showToast(err.message || 'Failed to update workflow role', 'error');
          }
          reRenderWorkflowTab();
          return;
        }
      });

      // Color swatch trigger + delete
      table.addEventListener('click', async (e) => {
        const trigger = e.target.closest('.wf-color-trigger');
        if (trigger) {
          const sid = trigger.dataset.id;
          const row = statuses.find(s => String(s.id) === String(sid));
          if (!row) return;
          openColorPicker(trigger, row.color, async (color) => {
            // Optimistic paint — chip + trigger recolor before the PATCH round-trip.
            const prevColor = row.color;
            row.color = color;
            trigger.style.setProperty('--status-color', `var(--c-${color})`);
            const chip = trigger.closest('.wf-chip');
            if (chip) chip.style.setProperty('--status-color', `var(--c-${color})`);
            try {
              await api.patch(`/projects/${id}/statuses/${sid}`, { color });
              invalidateStatuses(id);
              statuses = await loadStatuses(id);
            } catch (err) {
              row.color = prevColor;
              showToast(err.message || 'Failed to recolor status', 'error');
              try { statuses = await loadStatuses(id); } catch { /* keep optimistic revert */ }
            }
            reRenderWorkflowTab();
          });
          return;
        }

        const btn = e.target.closest('.wf-row-x');
        if (!btn || btn.disabled) return;
        const sid = btn.dataset.id;
        const row = statuses.find(s => String(s.id) === String(sid));
        if (!row) return;
        modalToken = openModal('Delete Status', `
          <p>Delete status <strong>${esc(statusLabel(row.name))}</strong>?</p>
          <div class="modal-footer">
            <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
            <button class="btn btn-danger" id="confirm-delete-status-btn">Delete</button>
          </div>`, {
          onMount(overlay) {
            overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);
            overlay.querySelector('#confirm-delete-status-btn').addEventListener('click', async () => {
              const cb = overlay.querySelector('#confirm-delete-status-btn');
              const enable = disableForSave(cb); // enable() also gives back keyboard focus
              cb.textContent = 'Deleting...';
              try {
                await api.delete(`/projects/${id}/statuses/${sid}`);
                invalidateStatuses(id);
                statuses = await loadStatuses(id);
                closeModal(); // gives keyboard focus back to this row's delete button
                showToast(`Deleted "${row.name}"`, 'success');
              } catch (err) {
                cb.textContent = 'Delete';
                enable();
                showToast(err.message || 'Failed to delete status', 'error');
                return;
              }
              const hadFocus = document.activeElement === btn;
              reRenderWorkflowTab();
              // The deleted row's delete button has no copy in the new markup, so focus on it fell
              // to the body: the add-status name field takes it instead (TPT9176).
              if (hadFocus && document.activeElement === document.body) main.querySelector('#wf-add-name')?.focus();
            });
          }
        });
      });
    }

    // Add form — same "fresh element every re-render" reasoning as bindWorkflowRowEvents.
    function bindWorkflowAddForm() {
      const wfAddForm = main.querySelector('#wf-add-form');
      if (!wfAddForm) return;

      const colorTrigger = main.querySelector('#wf-add-color-trigger');
      if (colorTrigger) {
        colorTrigger.addEventListener('click', () => {
          openColorPicker(colorTrigger, wfAddColor, (color) => {
            wfAddColor = color;
            colorTrigger.style.setProperty('--status-color', `var(--c-${color})`);
          });
        });
      }

      wfAddForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const nameInput = main.querySelector('#wf-add-name');
        const addBtn = main.querySelector('#wf-add-btn');
        const title = nameInput.value.trim();
        if (!title) return;
        // Status *names* must stay english snake_case slugs (matched by agents as a
        // workflow-role key) — the typed title can be in either project locale, so
        // transliterate it before it's ever sent to the API (C1190).
        const slug = transliterateToSlug(title);
        if (!slug) {
          showToast('Could not derive a valid status name from that title', 'error');
          return;
        }
        const enable = disableForSave(addBtn); // enable() also gives back keyboard focus
        try {
          await api.post(`/projects/${id}/statuses`, { name: slug, color: wfAddColor });
          invalidateStatuses(id);
          statuses = await loadStatuses(id);
          reRenderWorkflowTab();
          // The re-render replaced the disabled Add button: keyboard focus goes to the new one (TPT9174).
          enable();
          showToast(`Added "${statusLabel(slug)}"`, 'success');
        } catch (err) {
          showToast(err.message || 'Failed to add status', 'error');
          enable();
        }
      });
    }

    bindWorkflowRowEvents();
    bindWorkflowAddForm();

    // ── Sprints toggle (C1331) — autosave, same idiom as Language/Appearance above (no
    // Save button). Bound once here, not inside bindWorkflowRowEvents/reRenderWorkflowTab,
    // since the checkbox lives in its own sibling .settings-section outside the subtree
    // reRenderWorkflowTab() replaces — it never needs rebinding.
    const sprintsCb = main.querySelector('#settings-sprints-enabled');
    if (sprintsCb) {
      sprintsCb.addEventListener('change', async () => {
        const enabled = sprintsCb.checked;
        const enable = disableForSave(sprintsCb); // enable() also gives back keyboard focus
        try {
          const res = await api.patch('/projects/' + id, { sprints_enabled: enabled });
          if (res && res.project) project = res.project;
          invalidateProject(id); // C1395 — next visit to any other page re-fetches
          const groupField = main.querySelector('#task-group-label-group');
          if (groupField) groupField.hidden = !enabled;
          const durationField = main.querySelector('#sprint-duration-group');
          if (durationField) durationField.hidden = !enabled;
          const sortOrderField = main.querySelector('#sprint-sort-order-group');
          if (sortOrderField) sortOrderField.hidden = !enabled;
          // C1456 — toast follows the project's own grouping noun, not a hardcoded "Sprints".
          const plural = groupLabelPlural(project.task_group_label);
          showToast(enabled ? `${plural} shown` : `${plural} hidden`, 'success');
        } catch (err) {
          sprintsCb.checked = !enabled;
          showToast(err.message || 'Failed to save', 'error');
        }
        enable();
      });
    }

    // ── Sprint duration (TPT9107) — autosave, same idiom as sprintsCb above, except that the field
    // goes readOnly rather than disabled for the save: disabling drops focus to the body, so an
    // arrow key tapped again during the save would scroll the page. Lives in the Sprints card, not
    // #wf-section, so it survives reRenderWorkflowTab() and only needs binding once here.
    const sprintDurationInput = main.querySelector('#settings-sprint-duration');
    if (sprintDurationInput) {
      const sprintDurationError = main.querySelector('#sprint-duration-error');
      let savedSprintDuration = project.sprint_duration_days;
      bindSprintDurationSaves(sprintDurationInput, async () => {
        if (sprintDurationInput.readOnly) return; // a key let go during the save stepped nothing
        sprintDurationInput.readOnly = true;
        try {
          const saved = await saveSprintDuration(api, id, savedSprintDuration, sprintDurationInput, sprintDurationError);
          if (saved) {
            project = saved;
            savedSprintDuration = saved.sprint_duration_days;
            invalidateProject(id); // C1395 — the next loadProject() re-fetches the new duration
            showToast(sprintDurationText(saved.language).saved(groupLabelSingular(saved.task_group_label), savedSprintDuration), 'success');
          }
        } catch (err) {
          showToast(err.message || 'Failed to save', 'error');
        }
        sprintDurationInput.readOnly = false;
      });
    }

    // ── Objectives toggle (C1557) — autosave, same idiom as sprintsCb/kbSyncCb above (no
    // Save button). Bound once here — its own sibling .settings-section, never touched by
    // reRenderWorkflowTab().
    const objectiveGroupingCb = main.querySelector('#settings-use-objective-grouping');
    if (objectiveGroupingCb) {
      objectiveGroupingCb.addEventListener('change', async () => {
        const enabled = objectiveGroupingCb.checked; // real boolean — API rejects 0/1/'true'
        const enable = disableForSave(objectiveGroupingCb); // enable() also gives back keyboard focus
        try {
          const res = await api.patch('/projects/' + id, { use_objective_grouping: enabled });
          if (res && res.project) project = res.project;
          invalidateProject(id); // C1395 — next visit to any other page re-fetches
          showToast(enabled ? 'Objective task grouping enabled' : 'Objective task grouping disabled', 'success');
        } catch (err) {
          objectiveGroupingCb.checked = !enabled;
          showToast(err.message || 'Failed to save', 'error');
        }
        enable();
      });
    }

    // ── Group Tasks Into select (C1357) — autosave, same idiom as sprintsCb above and
    // Language/Appearance further up. Lives in the Sprints card (renderSprintsCard()), not
    // #wf-section, so — same reasoning as sprintsCb — it survives reRenderWorkflowTab()
    // untouched and only needs binding once here.
    const groupLabelSelect = main.querySelector('#settings-task-group-label');
    if (groupLabelSelect) {
      let prevGroupLabel = groupLabelSelect.value;
      groupLabelSelect.addEventListener('change', async () => {
        const value = groupLabelSelect.value;
        const enable = disableForSave(groupLabelSelect); // enable() also gives back keyboard focus
        // C1456 — repaint the Sprints card's captions immediately, before the PATCH
        // round-trip: picking "Bags" flips "Show Bags" etc. with no reload/save wait.
        paintSprintsCardCaptions(main, value, project.language);
        try {
          const res = await api.patch('/projects/' + id, { task_group_label: value });
          if (res && res.project) project = res.project;
          invalidateProject(id); // C1395 — next visit to any other page re-fetches
          prevGroupLabel = value;
          showToast(`Board groups now labeled "${groupLabelPlural(value)}"`, 'success');
        } catch (err) {
          groupLabelSelect.value = prevGroupLabel;
          paintSprintsCardCaptions(main, prevGroupLabel, project.language); // revert the optimistic repaint too
          showToast(err.message || 'Failed to save', 'error');
        }
        enable();
      });
    }

    // ── Sprint sort order select (C1577) — autosave, same idiom as groupLabelSelect above.
    // Lives in the Sprints card (renderSprintsCard()), not #wf-section, so it survives
    // reRenderWorkflowTab() untouched and only needs binding once here.
    const sprintSortOrderSelect = main.querySelector('#settings-sprint-sort-order');
    if (sprintSortOrderSelect) {
      let prevSprintSortOrder = sprintSortOrderSelect.value;
      sprintSortOrderSelect.addEventListener('change', async () => {
        const value = sprintSortOrderSelect.value;
        const enable = disableForSave(sprintSortOrderSelect); // enable() also gives back keyboard focus
        try {
          const res = await api.patch('/projects/' + id, { sprint_sort_order: value });
          if (res && res.project) project = res.project;
          invalidateProject(id); // C1395 — next visit to any other page re-fetches
          prevSprintSortOrder = value;
          showToast(value === 'asc' ? 'Board order set to ascending' : 'Board order set to descending', 'success');
        } catch (err) {
          sprintSortOrderSelect.value = prevSprintSortOrder;
          showToast(err.message || 'Failed to save', 'error');
        }
        enable();
      });
    }

    // Drag reorder — bound exactly ONCE on `document` (mousedown delegates off
    // `.wf-drag-handle`, mousemove/mouseup resolve `#wf-table` live via main.querySelector
    // rather than closing over a specific node) so a Workflow-tab re-render never stacks
    // a second document-level listener — the same leak guard board-filters.js's sprint
    // selector uses for its outside-click handler. House mouse-based drag pattern
    // (clone + placeholder), same idiom as pages/backlog.js; single container here, so
    // no cross-lane target logic is needed.
    const WF_DRAG_THRESHOLD = 5;
    // Keeps `drag` on the live table (relinkWorkflowDrag()); a drag whose status has no row any more
    // ends, unsaved. Whether a row drag is still running.
    function syncWorkflowDrag() {
      if (!drag) return false;
      if (relinkWorkflowDrag(main.querySelector('#wf-table'), drag)) return true;
      if (drag.clone) drag.clone.remove();
      if (drag.placeholder) drag.placeholder.remove();
      drag = null;
      return false;
    }
    _wfOnMouseDown = (e) => {
      const handle = e.target.closest('.wf-drag-handle');
      if (!handle) return;
      const row = handle.closest('.wf-row');
      if (!row) return;
      const rect = row.getBoundingClientRect();
      drag = {
        id: row.dataset.id, el: row, clone: null, placeholder: null,
        startX: e.clientX, startY: e.clientY,
        offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
        moved: false,
      };
      e.preventDefault();
    };
    _wfOnMouseMove = (e) => {
      // Before anything is built or measured: a save since the press replaced the pressed row.
      if (!syncWorkflowDrag()) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.moved) {
        if (Math.sqrt(dx * dx + dy * dy) < WF_DRAG_THRESHOLD) return;
        drag.moved = true;
        drag.clone = drag.el.cloneNode(true);
        drag.clone.classList.add('wf-row-dragging');
        drag.clone.style.width = `${drag.el.offsetWidth}px`;
        document.body.appendChild(drag.clone);
        drag.placeholder = document.createElement('div');
        drag.placeholder.className = 'wf-row-placeholder';
        drag.placeholder.style.height = `${drag.el.offsetHeight}px`;
        drag.el.parentNode.insertBefore(drag.placeholder, drag.el);
        drag.el.classList.add('wf-row-hidden');
      }
      drag.clone.style.left = `${e.clientX - drag.offsetX}px`;
      drag.clone.style.top = `${e.clientY - drag.offsetY}px`;
      const liveTable = main.querySelector('#wf-table');
      if (!liveTable) return;
      const rows = [...liveTable.querySelectorAll('.wf-row:not(.wf-row-hidden)')];
      let insertBefore = null;
      for (const r of rows) {
        const rect = r.getBoundingClientRect();
        if (e.clientY < rect.top + rect.height / 2) { insertBefore = r; break; }
      }
      if (insertBefore) liveTable.insertBefore(drag.placeholder, insertBefore);
      else liveTable.appendChild(drag.placeholder);
    };
    _wfOnMouseUp = async () => {
      if (!drag) return;
      const d = drag;
      drag = null;
      if (!d.moved) return;
      // Before the cleanup below un-hides the dragged row, like every other dropIndex() caller.
      const newIndex = workflowDropIndex(main.querySelector('#wf-table'), d.placeholder, d.id);
      if (d.clone) d.clone.remove();
      if (d.placeholder) d.placeholder.remove();
      d.el.classList.remove('wf-row-hidden');
      if (newIndex === -1) return;

      const before = statuses;
      const ordered = reorderedStatuses(statuses, d.id, newIndex);
      if (!ordered) return;
      const ids = ordered.map(s => s.id);
      statuses = ordered;
      reRenderWorkflowTab();

      try {
        const res = await api.put(`/projects/${id}/statuses/reorder`, { ids });
        invalidateStatuses(id);
        statuses = res.statuses || statuses;
      } catch (err) {
        showToast(err.message || 'Failed to save order', 'error');
        // The order from before the drag goes back on screen at once; then the server's, re-read
        // past the cache in case the PUT landed before it failed. A failed read leaves it as is.
        statuses = before;
        reRenderWorkflowTab();
        const saved = await reloadStatuses(id);
        if (saved) statuses = saved;
      }
      reRenderWorkflowTab();
    };
    document.addEventListener('mousedown', _wfOnMouseDown);
    document.addEventListener('mousemove', _wfOnMouseMove);
    document.addEventListener('mouseup', _wfOnMouseUp);
  }

  return () => {
    cleanupPermissionModes?.();
    cleanupWebhooks?.();
    closeModal(modalToken);
    closeColorPicker(); // C1189 — a color-swatch popover is appended to document.body, not this page's tree
    // Leak guard (C1182) — these 3 document-level listeners are added once per Settings
    // page visit (canManageSettings branch above); without detaching them here, navigating to
    // Settings repeatedly would stack a fresh set every time (same discipline as
    // board-filters.js's unbindBoardFilters()).
    if (_wfOnMouseDown) document.removeEventListener('mousedown', _wfOnMouseDown);
    if (_wfOnMouseMove) document.removeEventListener('mousemove', _wfOnMouseMove);
    if (_wfOnMouseUp) document.removeEventListener('mouseup', _wfOnMouseUp);
  };
}
